import { useAuth } from "react-oidc-context";

import { LEVEL_NAME, levelOf } from "../../auth/levels";
import { useAccess } from "../../auth/useAccess";
import { Button } from "../../f7";
import { Badge } from "../ui";

/**
 * Who you are acting as, and what that lets you do.
 *
 * The design's "Acting as L4 Owner" card. The level IS shown — the four Cognito
 * groups are strictly nested, so they already form the ladder the design names
 * and L1..L4 are its rungs (see auth/levels.ts, where a test asserts the
 * nesting rather than trusting it). The groups stay in view beneath it because
 * they are what the token actually carries, and "why is this greyed out" is the
 * most common question this console has to answer.
 *
 * What is NOT here is the design's Break-glass button, because the elevation
 * behind it does not exist:
 *
 *   - Authorities come from the `cognito:groups` claim, fixed when the token
 *     was minted. Nothing this page does can change that claim, so a button
 *     offering elevation would change no outcome — the server would refuse
 *     exactly as before, and the operator would read that refusal as a bug in
 *     the console rather than the correct answer.
 *   - The escalation path that does work: ask an admin-owner to add you to
 *     the group, then sign out and back in. That is a real, auditable change
 *     to a real claim.
 */
export function ActingAs() {
  const access = useAccess();
  const auth = useAuth();
  // Derived from the groups below, never stored — it cannot claim more than
  // the token grants.
  const level = levelOf(access);

  return (
    // The design's card: a ring inside the panel's gutter, not a full-bleed
    // band across it. Same shape, same rhythm as every other surface here.
    <div className="mx-2 rounded-lg p-3 ring-1 ring-slate-950/10 dark:ring-white/10">
      <p className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Acting as
      </p>
      <p className="mt-1 flex items-center gap-2 text-sm/5 font-medium text-slate-950 dark:text-white">
        {level} {LEVEL_NAME[level]}
        {level === "L0" && <Badge tone="amber">no access</Badge>}
      </p>
      <p className="mt-0.5 truncate text-xs/5 text-slate-500 dark:text-slate-400" title={access.email}>
        {access.email}
      </p>

      <p className="mt-0.5 text-xs/5 text-slate-500 dark:text-slate-400">
        {access.groups.length ? (
          access.groups.join(", ")
        ) : (
          // Not an error state, and not silent either: a signed-in user with
          // no group can read nothing and write nothing, and without this they
          // would meet that as a wall of inert controls with no explanation.
          <span className="text-amber-700 dark:text-amber-400">no group — you cannot act</span>
        )}
      </p>

      {access.unknownGroups.length > 0 && (
        // A group this console does not know grants nothing, and looks
        // identical to having no group. That is a deploy bug someone must fix,
        // so it is surfaced rather than ignored.
        <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
          Unrecognised: {access.unknownGroups.join(", ")}
        </p>
      )}

      {/* Where the design puts Break-glass — and, as there, a `plain` Button.
          The elevation behind it does not exist, so the slot carries the two
          things that do work: the map that explains the greyed-out control, and
          the way out. These were a Link and a bare `<button>` with two
          different text sizes and no focus ring, in the one place on the shell
          a keyboard user lands after the nav. */}
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <Button plain href="/governance/permissions">
          Your access
        </Button>
        <Button plain onClick={() => void auth.removeUser()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
